import { useEffect, useRef, useState } from 'react';
import { Camera, Check, ChevronDown, LogOut, Plus, Send, Settings, UserPlus, Users } from 'lucide-react';
import { AppChat, Permissao, type CanaisDto, type VinculoTelegramDto } from '@nexital/shared';
import { useDependencias, useEnvio, useQuadro } from '../../application/dependencias';
import { Avatar } from '../design-system/Avatar';
import { BuscaGeral } from './BuscaGeral';
import { useConfirmar } from '../design-system/Confirmacao';
import { MembrosDoQuadro } from '../configuracoes/MembrosDoQuadro';
import { DialogoConvidar, useOpcoesConvite } from './DialogoConvidar';
import { PainelTelegram } from './PainelTelegram';
import { Sino } from './Sino';
import { Marca } from '../design-system/Marca';
import { useMenuSuspenso } from './menu-suspenso';

type Props = { espaco: string; usuario: { nome: string; uuid: string } };

/** Seletor "Meu espaço": empresas e quadros onde a pessoa tem perfil (RQ-ACE-011). */
function SeletorEspaco({ rotulo }: { rotulo: string }) {
  const { espacos } = useDependencias();
  const menu = useMenuSuspenso();
  const escolher = (empresaUuid: string, quadroUuid: string) => {
    menu.fechar();
    espacos.navegar({ empresaUuid, quadroUuid });
  };

  return (
    <div className="menu-suspenso" ref={menu.ref}>
      <button type="button" className="barra-superior__espaco" aria-haspopup="menu" aria-expanded={menu.aberto} onClick={menu.alternar}>
        {rotulo} <ChevronDown size={16} aria-hidden />
      </button>
      {menu.aberto && (
        <div className="menu-suspenso__painel menu-suspenso__painel--esquerda" role="menu" aria-label="Meu espaço">
          {espacos.empresas.map((empresa) => (
            <div key={empresa.uuid} role="group" aria-label={empresa.nome}>
              <p className="menu-suspenso__titulo">{empresa.nome}</p>
              {empresa.quadros.length === 0 && <p className="menu-suspenso__vazio">Nenhum quadro com acesso.</p>}
              {empresa.quadros.map((quadro) => {
                const atual = quadro.uuid === espacos.atual.quadroUuid;
                return (
                  <button
                    key={quadro.uuid}
                    type="button"
                    role="menuitem"
                    className="menu-suspenso__item"
                    aria-current={atual}
                    onClick={() => escolher(empresa.uuid, quadro.uuid)}
                  >
                    {quadro.nome}
                    {atual && <Check size={16} aria-hidden style={{ marginLeft: 'auto' }} />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Vínculo do Telegram (RQ-MSG-007): o link abre o bot com um código de uso único; ao tocar em Iniciar a conversa
 * fica ligada à pessoa. Só aparece quando o bot está configurado no ambiente.
 */
function ItemTelegram() {
  const { espacos } = useDependencias();
  const avisar = useEnvio((s) => s.avisar);
  const confirmar = useConfirmar();
  const [canais, setCanais] = useState<CanaisDto | null>(null);
  const [painel, setPainel] = useState<VinculoTelegramDto | null>(null);
  // Com o painel aberto, confere a cada 4 s se a pessoa já tocou em Iniciar no celular.
  useEffect(() => {
    if (!painel) return;
    const conferir = async () => {
      const c = await espacos.canais.obter().catch(() => null);
      if (!c?.canais.some((x) => x.app === AppChat.Telegram)) return;
      setCanais(c);
      setPainel(null);
      avisar('Telegram vinculado. Os avisos chegam lá quando você estiver fora do Nexital.');
    };
    const relogio = setInterval(() => void conferir(), 4000);
    return () => clearInterval(relogio);
  }, [painel, espacos, avisar]);
  useEffect(() => {
    let ativo = true;
    espacos.canais.obter().then((c) => ativo && setCanais(c), () => undefined);
    return () => {
      ativo = false;
    };
  }, [espacos]);
  if (!canais?.telegramDisponivel) return null;
  const vinculado = canais.canais.some((c) => c.app === AppChat.Telegram);

  const vincular = async () => {
    try {
      const vinculo = await espacos.canais.vincularTelegram();
      // No celular o Telegram está ali: abre direto. No computador, QR code para o celular e WhatsApp.
      if (window.matchMedia('(pointer: coarse)').matches) window.location.assign(vinculo.link);
      else setPainel(vinculo);
    } catch (erro) {
      avisar((erro as Error).message);
    }
  };
  const desvincular = async () => {
    if (!(await confirmar({ titulo: 'Desvincular o Telegram?', mensagem: 'Os avisos deixam de chegar no Telegram. Dá para vincular de novo depois.', rotulo: 'Desvincular' }))) return;
    await espacos.canais.desvincularTelegram();
    setCanais(await espacos.canais.obter());
    avisar('Telegram desvinculado.');
  };

  return (
    <>
      {vinculado ? (
        <button type="button" role="menuitem" className="menu-suspenso__item" onClick={() => void desvincular()} title="Você recebe os avisos no Telegram">
          <Send size={16} aria-hidden /> Desvincular Telegram
          <Check size={16} aria-hidden style={{ marginLeft: 'auto' }} />
        </button>
      ) : (
        <button type="button" role="menuitem" className="menu-suspenso__item" onClick={() => void vincular()}>
          <Send size={16} aria-hidden /> Vincular Telegram
        </button>
      )}
      {painel && <PainelTelegram link={painel.link} expiraEm={painel.expiraEm} aoFechar={() => setPainel(null)} />}
    </>
  );
}

/** Convidar para o quadro (ADR-0021): só aparece para quem pode convidar algum perfil (o leitor não vê). */
function ItemConvidar({ visivel, abrir }: { visivel: boolean; abrir: () => void }) {
  if (!visivel) return null;
  return (
    <button type="button" role="menuitem" className="menu-suspenso__item" onClick={abrir}>
      <UserPlus size={16} aria-hidden /> Convidar para o quadro
    </button>
  );
}

/** Diálogo do convite fora do painel do menu (o menu fecha ao abrir a caixa); o resultado vira aviso do sistema. */
function useConvite(menuAberto: boolean) {
  const opcoes = useOpcoesConvite(menuAberto);
  const avisar = useEnvio((s) => s.avisar);
  const [aberto, definirAberto] = useState(false);
  const dialogo =
    aberto && opcoes ? (
      <DialogoConvidar
        opcoes={opcoes}
        aoFechar={(mensagem) => {
          definirAberto(false);
          if (mensagem) avisar(mensagem);
        }}
      />
    ) : null;
  return { pode: Boolean(opcoes?.perfis.length), abrir: () => definirAberto(true), dialogo };
}

/** Janela dos membros do quadro (RQ-ACE-018), fora do painel do menu (o menu fecha ao abrir). */
function useJanelaMembros(fecharMenu: () => void) {
  const [aberta, definirAberta] = useState(false);
  const item = (
    <button
      type="button"
      role="menuitem"
      className="menu-suspenso__item"
      onClick={() => {
        definirAberta(true);
        fecharMenu();
      }}
    >
      <Users size={16} aria-hidden /> Membros do quadro
    </button>
  );
  return { item, janela: aberta ? <MembrosDoQuadro aoFechar={() => definirAberta(false)} /> : null };
}

/** Avatar com o nome real e menu da conta: foto, convidar, membros do quadro, Telegram e "Sair" (todas as abas). */
function MenuConta({ usuario }: { usuario: { nome: string; uuid: string } }) {
  const { espacos } = useDependencias();
  const menu = useMenuSuspenso();
  const convite = useConvite(menu.aberto);
  const membros = useJanelaMembros(menu.alternar);
  const seletor = useRef<HTMLInputElement>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const trocarFoto = async (arquivo: File | undefined) => {
    if (!arquivo) return;
    setAviso('Enviando foto…');
    try {
      await espacos.enviarFoto(arquivo);
      setAviso(null);
    } catch (erro) {
      setAviso((erro as Error).message);
    }
  };
  return (
    <div className="menu-suspenso" ref={menu.ref}>
      <button
        type="button"
        className="barra-superior__avatar"
        aria-label={`Conta de ${usuario.nome}`}
        aria-haspopup="menu"
        aria-expanded={menu.aberto}
        onClick={menu.alternar}
      >
        <Avatar nome={usuario.nome} chave={usuario.uuid} tamanho={36} url={espacos.usuario.avatarUrl} />
      </button>
      {menu.aberto && (
        <div className="menu-suspenso__painel menu-suspenso__painel--direita" role="menu" aria-label="Conta">
          <div className="menu-suspenso__conta">
            <strong>{usuario.nome}</strong>
            <span>{espacos.usuario.email}</span>
          </div>
          <button type="button" role="menuitem" className="menu-suspenso__item" onClick={() => seletor.current?.click()}>
            <Camera size={16} aria-hidden /> Trocar foto
          </button>
          <input ref={seletor} type="file" hidden accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => void trocarFoto(e.target.files?.[0])} />
          <ItemConvidar
            visivel={convite.pode}
            abrir={() => {
              convite.abrir();
              menu.alternar();
            }}
          />
          {membros.item}
          <ItemTelegram />
          {aviso && <p className="menu-suspenso__aviso" role="status">{aviso}</p>}
          <button type="button" role="menuitem" className="menu-suspenso__item" onClick={espacos.sair}>
            <LogOut size={16} aria-hidden /> Sair
          </button>
        </div>
      )}
      {convite.dialogo}
      {membros.janela}
    </div>
  );
}

/** Engrenagem: abre as Configurações (ADR-0022); as seções dependem do papel na empresa. */
function BotaoConfiguracoes() {
  const abrir = useEnvio((s) => s.abrirConfiguracoes);
  return (
    <button type="button" className="barra-superior__icone" aria-label="Configurações" title="Configurações" onClick={() => abrir('inicio')}>
      <Settings size={20} />
    </button>
  );
}

/** Enviar arquivos (ADR-0020): só para quem envia documentos. O diálogo fica na tela do quadro. */
function BotaoEnviarArquivos() {
  const pode = useQuadro((s) => s.dados?.permissoes.includes(Permissao.EnviarDocumentos) ?? false);
  const abrir = useEnvio((s) => s.abrirDialogo);
  return (
    <button type="button" className="botao-primario" disabled={!pode} onClick={() => abrir()}>
      <Plus size={18} aria-hidden /> Enviar arquivos
    </button>
  );
}

/** Barra superior fixa (referências A, B e C). Busca e nova licitação chegam nos próximos incrementos. */
export function BarraSuperior({ espaco, usuario }: Props) {
  return (
    <header className="barra-superior">
      <div className="barra-superior__marca" aria-label="Nexital">
        <Marca />
      </div>
      <SeletorEspaco rotulo={espaco} />
      <BuscaGeral />
      <BotaoEnviarArquivos />
      <Sino />
      <BotaoConfiguracoes />
      <MenuConta usuario={usuario} />
    </header>
  );
}
